/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useMemo, cloneElement } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiFormControlLayoutDelimited } from '../form';
import { useEuiTheme } from '../../services';
import { euiDatePickerRangeStyles, euiDatePickerRangeInlineStyles } from './date_picker_range.styles';
export var EuiDatePickerRange = ({
  children,
  className,
  startDateControl,
  endDateControl,
  iconType = true,
  inline,
  shadow = true,
  fullWidth: _fullWidth,
  compressed: _compressed,
  isCustom,
  readOnly,
  isLoading,
  isInvalid,
  disabled,
  onFocus,
  onBlur,
  append,
  prepend,
  ...rest
}) => {
  // `fullWidth` and `compressed` should not affect inline datepickers (matches non-range behavior)
  const fullWidth = _fullWidth && !inline;
  const compressed = _compressed && !inline;
  const classes = classNames('euiDatePickerRange', className);
  const euiTheme = useEuiTheme();
  const styles = euiDatePickerRangeStyles(euiTheme);
  const cssStyles = [styles.euiDatePickerRange];
  if (inline) {
    // Determine the inline container query to use based on the width of the react-datepicker
    const hasTimeSelect = startDateControl.props.showTimeSelect || endDateControl.props.showTimeSelect;
    const inlineStyles = euiDatePickerRangeInlineStyles(euiTheme);
    cssStyles.push(inlineStyles.inline);
    cssStyles.push(hasTimeSelect ? inlineStyles.responsiveWithTimeSelect : inlineStyles.responsive);
    if (shadow) cssStyles.push(inlineStyles.shadow);
  }
  let startControl = startDateControl;
  let endControl = endDateControl;
  if (!isCustom) {
    startControl = cloneElement(startDateControl, {
      controlOnly: true,
      showIcon: false,
      inline,
      fullWidth,
      readOnly,
      disabled: disabled || startDateControl.props.disabled,
      isInvalid: isInvalid || startDateControl.props.isInvalid,
      className: classNames('euiDatePickerRange__start', startDateControl.props.className),
      onBlur: event => {
        startDateControl.props?.onBlur?.(event);
        onBlur?.(event);
      },
      onFocus: event => {
        startDateControl.props?.onFocus?.(event);
        onFocus?.(event);
      }
    });
    endControl = cloneElement(endDateControl, {
      controlOnly: true,
      showIcon: false,
      inline,
      fullWidth,
      readOnly,
      disabled: disabled || endDateControl.props.disabled,
      isInvalid: isInvalid || endDateControl.props.isInvalid,
      popoverPlacement: 'downRight',
      className: classNames('euiDatePickerRange__end', endDateControl.props.className),
      onBlur: event => {
        endDateControl.props?.onBlur?.(event);
        onBlur?.(event);
      },
      onFocus: event => {
        endDateControl.props?.onFocus?.(event);
        onFocus?.(event);
      }
    });
  }
  const icon = useMemo(() => {
    if (inline) return undefined;
    if (iconType === false) return undefined;
    if (iconType === true) return 'calendar';
    return iconType;
  }, [iconType, inline]);
  return <span className={classes} css={cssStyles} {...rest}>
      <EuiFormControlLayoutDelimited icon={icon} startControl={startControl} endControl={endControl} fullWidth={fullWidth} compressed={compressed} readOnly={readOnly} isDisabled={disabled} isInvalid={isInvalid} isLoading={isLoading} append={inline ? undefined : append} prepend={inline ? undefined : prepend} />
    </span>;
};
EuiDatePickerRange.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * Including any children will replace all innards with the provided children
       */
  children: PropTypes.node,
  /**
       * The end date `EuiDatePicker` element
       */
  endDateControl: PropTypes.element.isRequired,
  /**
       * The start date `EuiDatePicker` element
       */
  startDateControl: PropTypes.element.isRequired,
  /**
       * Pass either an icon type or set to `false` to remove icon entirely
       */
  iconType: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired]),
  /**
       * Won't apply any additional props to start and end date components
       */
  isCustom: PropTypes.bool,
  /**
       * Passes through to each control
       */
  disabled: PropTypes.bool,
  /**
       * Displays both date picker calendars directly on the page.
       * Will not render `iconType`, `fullWidth`, `prepend`, or `append`.
       *
       * Passes through to each control if `isCustom` is not set.
       */
  inline: PropTypes.bool,
  /**
       * Allows turning the shadow off if using the `inline` prop
       */
  shadow: PropTypes.bool,
  /**
       * Triggered whenever the start or end controls are blurred
       */
  onBlur: PropTypes.any,
  /**
       * Triggered whenever the start or end controls are focused
       */
  onFocus: PropTypes.any
};
try {
  EuiDatePickerRange.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/date_picker/date_picker_range.tsx',
    description: '',
    displayName: 'EuiDatePickerRange',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      append: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming after children.\n' + '`string` | `ReactElement` or an array of these',
        name: 'append',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_control_layout/form_control_layout.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      prepend: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming before children.\n' + '`string` | `ReactElement` or an array of these',
        name: 'prepend',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_control_layout/form_control_layout.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'Expand to fill 100% of the parent.\n' + 'Defaults to `fullWidth` prop of `<EuiForm>`.',
        name: 'fullWidth',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_control_layout/form_control_layout.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_control_layout/form_control_layout.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_control_layout/form_control_layout.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      readOnly: {
        defaultValue: null,
        description: '',
        name: 'readOnly',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_control_layout/form_control_layout.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: '',
        name: 'isInvalid',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_control_layout/form_control_layout.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      children: {
        defaultValue: null,
        description: 'Including any children will replace all innards with the provided children',
        name: 'children',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      endDateControl: {
        defaultValue: null,
        description: 'The end date `EuiDatePicker` element',
        name: 'endDateControl',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactElement'
        }
      },
      startDateControl: {
        defaultValue: null,
        description: 'The start date `EuiDatePicker` element',
        name: 'startDateControl',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactElement'
        }
      },
      iconType: {
        defaultValue: {
          value: 'true'
        },
        description: 'Pass either an icon type or set to `false` to remove icon entirely',
        name: 'iconType',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean | IconType'
        }
      },
      isCustom: {
        defaultValue: null,
        description: "Won't apply any additional props to start and end date components",
        name: 'isCustom',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      disabled: {
        defaultValue: null,
        description: 'Passes through to each control',
        name: 'disabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      inline: {
        defaultValue: null,
        description: 'Displays both date picker calendars directly on the page.\n' + 'Will not render `iconType`, `fullWidth`, `prepend`, or `append`.\n' + '\n' + 'Passes through to each control if `isCustom` is not set.',
        name: 'inline',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      shadow: {
        defaultValue: {
          value: 'true'
        },
        description: 'Allows turning the shadow off if using the `inline` prop',
        name: 'shadow',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onBlur: {
        defaultValue: null,
        description: 'Triggered whenever the start or end controls are blurred',
        name: 'onBlur',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLInputElement>'
        }
      },
      onFocus: {
        defaultValue: null,
        description: 'Triggered whenever the start or end controls are focused',
        name: 'onFocus',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/date_picker_range.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLInputElement>'
        }
      }
    },
    extendedInterfaces: ['CommonProps']
  };
} catch (e) {}